// ╔══════════════════════════════════════════════════════════════╗
// ║            CourseHubDashboard.jsx                            ║
// ║                                                              ║
// ║  What this file does:                                        ║
// ║    Renders the main learning dashboard page.                 ║
// ║                                                              ║
// ║  FIXED (this pass):                                           ║
// ║    - The real bug: every logged-in user saw every course in   ║
// ║      every category, regardless of what track they're        ║
// ║      enrolled in. Now gated through courseAccess.js, fed by   ║
// ║      AuthHelpers.getUserAccess() (Firestore users/{uid}).      ║
// ║    - Two courses ("Design Patterns", "Clean Code") were        ║
// ║      mistagged category: "Frontend" — that would have hidden   ║
// ║      them from backend/database students under the new        ║
// ║      gating, even though they're meant to be visible to        ║
// ║      everyone. Recategorized to "Core Practices" (shared).     ║
// ║    - Fixed a casing bug ("systems" vs "Systems", "ai" vs       ║
// ║      "AI") that would have made two categories fail to match   ║
// ║      their shared-category entries.                            ║
// ║    - Converted every `function X()` declaration to the         ║
// ║      arrow-function style used elsewhere in this codebase.     ║
// ║                                                              ║
// ║  STILL UNRESOLVED — see the note above ALL_COURSES: nothing    ║
// ║  currently WRITES enrolledTracks/supportLevel/allAccess to a   ║
// ║  user's Firestore doc. Until the signup/payment flow does      ║
// ║  that, every user reads as zero access and sees only the       ║
// ║  shared/cross-cutting courses. That's a safe failure mode      ║
// ║  (nothing leaks) but it means this page alone does not         ║
// ║  finish the feature — see AuthHelpers.js's getUserAccess()     ║
// ║  doc comment for what needs to write those fields.             ║
// ╚══════════════════════════════════════════════════════════════╝

import React, { useState, useMemo, useEffect } from "react";

// MUI layout & display components
import {
    Box, Grid, Typography, Avatar, Chip,
    Card, CardContent, Divider,
    InputAdornment, TextField,
    Fab, Zoom,
    IconButton, Drawer, List, ListItem, ListItemText, ListItemIcon,
} from "@mui/material";

// MUI styling utilities
import { styled, keyframes, useTheme, useMediaQuery } from "@mui/material";

// MUI icons
import SchoolIcon              from "@mui/icons-material/School";
import CodeIcon                from "@mui/icons-material/Code";
import GroupIcon               from "@mui/icons-material/Group";
import QuizIcon                from "@mui/icons-material/Quiz";
import AssignmentIcon          from "@mui/icons-material/Assignment";
import EmojiEventsIcon         from "@mui/icons-material/EmojiEvents";
import SearchIcon              from "@mui/icons-material/Search";
import KeyboardArrowUpIcon     from "@mui/icons-material/KeyboardArrowUp";
import MenuIcon                from "@mui/icons-material/Menu";
import CloseIcon               from "@mui/icons-material/Close";
import TrendingUpIcon          from "@mui/icons-material/TrendingUp";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import WbSunnyIcon             from "@mui/icons-material/WbSunny";
import NightsStayIcon          from "@mui/icons-material/NightsStay";
import FilterListIcon          from "@mui/icons-material/FilterList";
import LockIcon                from "@mui/icons-material/Lock";

// React Router
import { Link } from "react-router-dom";

// Recharts
import {
    AreaChart, Area,
    BarChart, Bar,
    XAxis, YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    ResponsiveContainer,
} from "recharts";

// Project-specific
import { useSimulatedStats } from "../Projects/Hook/useSimulatedStats";
import LectureTimer           from "../../components/LectureTimer";
import { getUserAccess }      from "../../utils/AuthHelpers";
import { canAccessCourse, visibleCategoriesFor } from "../../utils/courseAccess";

// ─────────────────────────────────────────────────────────────
// 2. DESIGN TOKENS
// ─────────────────────────────────────────────────────────────
const COLOR = {
    gold:        "goldenrod",
    goldBright:  "#f5c518",
    goldGlow:    "rgba(218,165,32,0.18)",
    goldBorder:  "rgba(218,165,32,0.25)",
    goldHint:    "rgba(218,165,32,0.06)",
    pageBg:      "#080614",
    cardBg:      "rgba(255,255,255,0.04)",
    textPrimary:   "#ffffff",
    textSecondary: "rgba(255,255,255,0.55)",
    textFaint:     "rgba(255,255,255,0.25)",
    green:  "#4ade80",
    blue:   "#60a5fa",
    purple: "#a78bfa",
    rose:   "#fb7185",
    orange: "#fb923c",
};

const FONT = "'Nunito', 'Segoe UI', sans-serif";

// ─────────────────────────────────────────────────────────────
// 3. KEYFRAME ANIMATIONS
// ─────────────────────────────────────────────────────────────
const fadeSlideUp = keyframes`
    from { opacity: 0;  transform: translateY(26px); }
    to   { opacity: 1;  transform: translateY(0);    }
`;

const floatOrb = keyframes`
    0%,100% { transform: translate(0,    0)    scale(1);    }
    33%      { transform: translate(18px,-14px) scale(1.04); }
    66%      { transform: translate(-9px, 9px)  scale(0.97); }
`;

const livePulse = keyframes`
    0%,100% { opacity: 1;   }
    50%      { opacity: 0.2; }
`;

// ─────────────────────────────────────────────────────────────
// 4. STYLED COMPONENTS
// ─────────────────────────────────────────────────────────────
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight:     "100vh",
    fontFamily:    FONT,
    color:         COLOR.textPrimary,
    paddingTop:    theme.spacing(10),
    paddingBottom: theme.spacing(10),
    position:      "relative",
    overflowX:     "hidden",
    background: `
        radial-gradient(ellipse at 15% 5%,  rgba(218,165,32,0.08) 0%, transparent 55%),
        radial-gradient(ellipse at 88% 88%, rgba(139,92,246,0.07) 0%, transparent 55%),
        linear-gradient(170deg, #080614 0%, #0f0a1e 55%, #060410 100%)
    `,
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(11),
    },
}));

const BackgroundOrb = styled(Box)(({ size = 400 }) => ({
    position:      "absolute",
    width:         size,
    height:        size,
    borderRadius:  "50%",
    pointerEvents: "none",
    zIndex:        0,
    animation:     `${floatOrb} 14s ease-in-out infinite`,
}));

const GlassCard = styled(Card)({
    fontFamily:   FONT,
    background:   COLOR.cardBg,
    border:       `1px solid ${COLOR.goldBorder}`,
    borderRadius: "20px",
    boxShadow:    "0 4px 30px rgba(0,0,0,0.3)",
    color:        COLOR.textPrimary,
    overflow:     "hidden",
    position:     "relative",
    transition:   "transform 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease",
    "&::before": {
        content:    '""',
        position:   "absolute",
        top: 0, left: "12%", right: "12%",
        height:     "1px",
        background: `linear-gradient(to right, transparent, ${COLOR.gold}55, transparent)`,
    },
    "&:hover": {
        transform:   "translateY(-5px)",
        borderColor: `${COLOR.gold}66`,
        boxShadow:   `0 16px 40px rgba(218,165,32,0.12)`,
    },
});

const CourseTile = styled(Box)({
    display:      "flex",
    alignItems:   "center",
    gap:          "12px",
    background:   COLOR.cardBg,
    border:       `1px solid ${COLOR.goldBorder}`,
    borderRadius: "14px",
    padding:      "14px 16px",
    cursor:       "pointer",
    position:     "relative",
    overflow:     "hidden",
    transition:   "all 0.22s ease",
    "&:hover": {
        background:  `linear-gradient(135deg, rgba(218,165,32,0.13), rgba(218,165,32,0.03))`,
        borderColor: `${COLOR.gold}88`,
        transform:   "translateY(-3px)",
        boxShadow:   `0 8px 28px rgba(218,165,32,0.14)`,
    },
});

const FeatureTile = styled(Box)({
    fontFamily:   FONT,
    background:   COLOR.cardBg,
    border:       `1px solid ${COLOR.goldBorder}`,
    borderRadius: "20px",
    padding:      "36px 20px",
    textAlign:    "center",
    cursor:       "pointer",
    height:       "100%",
    transition:   "all 0.25s ease",
    "&:hover": {
        background:  `linear-gradient(160deg, rgba(218,165,32,0.14), rgba(218,165,32,0.03))`,
        borderColor: `${COLOR.gold}66`,
        transform:   "translateY(-6px)",
        boxShadow:   `0 16px 44px rgba(218,165,32,0.13)`,
    },
});

const SearchInput = styled(TextField)({
    "& .MuiOutlinedInput-root": {
        fontFamily:   FONT,
        color:        COLOR.textPrimary,
        fontSize:     "0.9rem",
        background:   "rgba(255,255,255,0.04)",
        borderRadius: "50px",
        "& fieldset":             { borderColor: COLOR.goldBorder, borderRadius: "50px" },
        "&:hover fieldset":       { borderColor: `${COLOR.gold}88` },
        "&.Mui-focused fieldset": { borderColor: COLOR.gold, boxShadow: `0 0 0 3px ${COLOR.goldGlow}` },
    },
    "& .MuiInputBase-input::placeholder": {
        color: COLOR.textSecondary, opacity: 1,
    },
});

const CategoryChip = styled(Chip, {
    shouldForwardProp: (prop) => prop !== "isActive",
})(({ isActive }) => ({
    fontFamily: FONT,
    fontWeight: 600,
    fontSize:   "0.72rem",
    cursor:     "pointer",
    height:     28,
    transition: "all 0.18s ease",
    ...(isActive ? {
        background: COLOR.gold,
        color:      COLOR.pageBg,
        border:     `1px solid ${COLOR.gold}`,
        "&:hover":  { background: COLOR.goldBright },
    } : {
        background: "rgba(255,255,255,0.07)",
        color:      COLOR.textSecondary,
        border:     "1px solid rgba(255,255,255,0.1)",
        "&:hover":  { background: "rgba(255,255,255,0.12)", color: COLOR.textPrimary },
    }),
}));

const AnimateIn = styled(Box, {
    shouldForwardProp: (prop) => prop !== "delay",
})(({ delay = 0 }) => ({
    opacity:   0,
    animation: `${fadeSlideUp} 0.55s ease ${delay}s both`,
}));

// ─────────────────────────────────────────────────────────────
// 5. STATIC DATA
//
// `category` is the gating key courseAccess.js reads — keep it in
// sync with TRACK_CATEGORY_MAP / SHARED_CATEGORIES there whenever a
// course is added or recategorized here.
// ─────────────────────────────────────────────────────────────

const ALL_COURSES = [
    { label: "JavaScript",       link: "/javascriptcoding",       category: "Web"     },
    { label: "TypeScript",       link: "/typescript-dashboard",   category: "Web"     },
    { label: "Java",             link: "/javadashboard",          category: "Backend" },
    { label: "Express.js",       link: "/express-dashboard",      category: "Backend" },
    { label: "Spring Boot",      link: "/springboot-dashboard",   category: "Backend" },
    // C++'s category is still "Systems" (shared) — Hazzan's last
    // message was cut off before saying what it should become;
    // asked him to confirm before touching it.
    { label: "C++",              link: "/cpp-dashboard",          category: "Systems" },
    // RECATEGORIZED (Hazzan's request): was "Systems" — grouped with
    // the other DevOps/tooling courses instead. Still shared either
    // way (Systems and DevOps are both in SHARED_CATEGORIES), so
    // this is a grouping fix, not an access change.
    { label: "EmbeddedC-IoT",    link: "/iot-dashboard",           category: "DevOps" },
    { label: "Python",           link: "/pythonCodingDashBoard",  category: "Data"    },
    { label: "MySQL",            link: "/mysql-dashboard",        category: "Data"    },
    { label: "MongoDB",          link: "/mongodb",                category: "Data"    },
    { label: "Github",           link: "/git-dashboard",          category: "DevOps"  },
    { label: "Linux",            link: "/linux-dashboard",        category: "DevOps"  },
    { label: "Docker",           link: "/docker",                 category: "DevOps"  },
    // RECATEGORIZED (Hazzan's request): was "QA" — moved under
    // DevOps. Also just a grouping fix (QA and DevOps are both
    // shared), not an access change.
    { label: "Software-Testing", link: "/software-testing",       category: "DevOps" },
    { label: "HTML5",            link: "/html5-dashboard",       category: "Frontend"},
    // RECATEGORIZED AGAIN (Hazzan's request, this pass): these were
    // moved to shared "Core Practices" last pass, but Hazzan now
    // wants them under "Backend" specifically. REAL ACCESS CHANGE:
    // Backend is NOT a shared category, so frontend-only and
    // database-only students will stop seeing these two — only
    // backend/fullstack-track students (or All-Access) will from
    // here on. Flagged back to Hazzan; applying as requested.
    { label: "Design Patterns",  link: "/design-patterns-dashboard",       category: "Backend"},
    { label: "Clean Code",       link: "/clean-code-dashboard",       category: "Backend"},
    { label: "CSS",              link: "/css-dashboard",       category: "Frontend"},
    { label: "UI/UX Dashboard",  link: "/uiux-dashboard",       category: "Frontend"  },
    // FIXED: was category:"systems" (lowercase) — didn't match the
    // "Systems" used by C++ above, so it silently wouldn't have
    // matched a shared-category entry either.
    { label: "System Design",  link: "/system-design-dashboard",       category: "Systems"  },
    { label: "Google Cloud",  link: "/gcp-dashboard",                  category: "Cloud"  },
    // FIXED: was category:"ai" (lowercase) — normalized casing to
    // match the rest of this list.
    { label: "AI & LLM",  link: "/ai-dashboard",                  category: "AI"  },
    { label: "React Course",  link: "/react-dashboard",                  category: "Frontend"  },
    // RECATEGORIZED (Hazzan's request): was "Project-Guide" (shared).
    // REAL ACCESS CHANGE: now "Backend", so only backend/fullstack-
    // track students (or All-Access) will see it going forward.
    { label: "SpringBoot Architecture",  link: "/springboot-architecture",                  category: "Backend"  },

];

const FEATURE_CARDS = [
    { icon: <QuizIcon />,        label: "Learning Journey", link: "/learning-journey",         desc: "Track your progress over time",     color: COLOR.blue   },
    { icon: <AssignmentIcon />,  label: "Assignments",      link: "/assignment-dashboard",          desc: "Practice problems & exercises",     color: COLOR.green  },
    { icon: <CodeIcon />,        label: "Answers",          link: "/java-assignment-solutions", desc: "Reviewed solution walkthroughs",    color: COLOR.purple },
    { icon: <EmojiEventsIcon />, label: "Final Projects",   link: "/project-list",              desc: "Showcase your completed work",      color: COLOR.rose   },
];

const DAILY_TIPS = [
    "Practice 25 min then rest 5 — the Pomodoro technique boosts retention.",
    "Type code examples by hand. Muscle memory accelerates recall.",
    "Explain what you learned out loud — the Feynman technique works.",
    "Commit to GitHub daily. Small, consistent pushes build great habits.",
    "Read error messages carefully — they almost always tell you the fix.",
    "Review yesterday's notes before starting today's new topic.",
    "Build something small with each concept you learn. Doing beats reading.",
];

// ─────────────────────────────────────────────────────────────
// 6. PURE HELPER FUNCTIONS
// ─────────────────────────────────────────────────────────────
const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return { text: "Good morning",   icon: <WbSunnyIcon   sx={{ fontSize: 16, color: COLOR.gold   }} /> };
    if (hour < 18) return { text: "Good afternoon", icon: <WbSunnyIcon   sx={{ fontSize: 16, color: COLOR.gold   }} /> };
    return              { text: "Good evening",   icon: <NightsStayIcon sx={{ fontSize: 16, color: COLOR.purple }} /> };
};

const getTodaysTip = () => DAILY_TIPS[new Date().getDay() % DAILY_TIPS.length];

const buildStatCards = (stats) => [
    { icon: <GroupIcon />,       color: COLOR.green,  label: "Active Users",      value: stats.activeUsers ?? 0,                       caption: "+12% this week"       },
    { icon: <SchoolIcon />,      color: COLOR.gold,   label: "Total Courses",     value: stats.totalCourses ?? 0,                      caption: "Across 6 categories"  },
    { icon: <CodeIcon />,        color: COLOR.blue,   label: "Projects",          value: stats.projects ?? 0,                          caption: "+3 submitted today"   },
    { icon: <EmojiEventsIcon />, color: COLOR.rose,   label: "Completions",       value: Math.round((stats.activeUsers ?? 0) * 0.4),   caption: "Certificates earned"  },
];

// ─────────────────────────────────────────────────────────────
// 7. SMALL UI COMPONENTS
// ─────────────────────────────────────────────────────────────

const ScrollToTopButton = () => {
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        const onScroll = () => setIsVisible(window.pageYOffset > 300);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    return (
        <Zoom in={isVisible}>
            <Fab
                onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                size="small"
                sx={{
                    position:  "fixed",
                    bottom:    24,
                    right:     24,
                    zIndex:    1400,
                    bgcolor:   COLOR.gold,
                    color:     COLOR.pageBg,
                    boxShadow: `0 4px 20px rgba(218,165,32,0.45)`,
                    "&:hover": { bgcolor: COLOR.goldBright },
                }}
            >
                <KeyboardArrowUpIcon />
            </Fab>
        </Zoom>
    );
};

const ChartTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
        <Box sx={{
            fontFamily:   FONT,
            bgcolor:      "#0f0a1e",
            border:       `1px solid ${COLOR.goldBorder}`,
            borderRadius: "12px",
            p:            "10px 16px",
        }}>
            <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: COLOR.gold, mb: 0.5 }}>{label}</Typography>
            {payload.map((entry, i) => (
                <Typography key={i} sx={{ fontSize: "0.82rem", color: COLOR.textPrimary }}>
                    {entry.name}: <strong style={{ color: COLOR.gold }}>{entry.value}</strong>
                </Typography>
            ))}
        </Box>
    );
};

const SectionHeader = ({ eyebrow, title, delay = 0 }) => (
    <AnimateIn delay={delay} sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: "0.63rem", fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: COLOR.gold, mb: 0.5 }}>{eyebrow}</Typography>
        <Typography variant="h5" sx={{ fontWeight: 800, color: COLOR.textPrimary }}>{title}</Typography>
    </AnimateIn>
);

const StatCard = ({ icon, color, label, value, caption, delay = 0 }) => (
    <AnimateIn delay={delay}>
        <GlassCard>
            <CardContent sx={{ p: "22px !important" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                    <Avatar sx={{ bgcolor: `${color}22`, color, width: 44, height: 44 }}>
                        {React.cloneElement(icon, { sx: { fontSize: 22 } })}
                    </Avatar>
                    <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: COLOR.textSecondary }}>{label}</Typography>
                </Box>
                <Typography sx={{ fontSize: "2.1rem", fontWeight: 900, color, lineHeight: 1, mb: 0.75 }}>{value}</Typography>
                <Typography sx={{ fontSize: "0.72rem", color: COLOR.textFaint }}>{caption}</Typography>
            </CardContent>
        </GlassCard>
    </AnimateIn>
);

// ─────────────────────────────────────────────────────────────
// 8. MAIN COMPONENT
// ─────────────────────────────────────────────────────────────
const CourseHubDashboard = ({ username = "Hazzan" }) => {

    useEffect(() => {
        const link  = document.createElement("link");
        link.rel    = "stylesheet";
        link.href   = "https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&display=swap";
        document.head.appendChild(link);
    }, []);

    const { stats, userHistory, projectHistory } = useSimulatedStats();
    const theme    = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const [searchQuery,    setSearchQuery]    = useState("");
    const [activeCategory, setActiveCategory] = useState("All");
    const [drawerOpen,     setDrawerOpen]     = useState(false);

    // Access starts at "nothing" so there is never a frame where an
    // unauthorized course is visible while the Firestore read is in
    // flight. accessLoaded just controls the loading message below —
    // it does not gate anything by itself.
    const [access, setAccess] = useState({ enrolledTracks: [], supportLevel: null, allAccess: false });
    const [accessLoaded, setAccessLoaded] = useState(false);

    useEffect(() => {
        let cancelled = false;
        getUserAccess().then((result) => {
            if (!cancelled) {
                setAccess(result);
                setAccessLoaded(true);
            }
        });
        return () => { cancelled = true; };
    }, []);

    const greeting  = getTimeGreeting();
    const tip       = getTodaysTip();
    const statCards = buildStatCards(stats);
    const streak    = 7;

    // Gate BEFORE anything else touches ALL_COURSES — search and the
    // category chips should only ever operate on what this user is
    // actually allowed to see.
    const accessibleCourses = useMemo(
        () => ALL_COURSES.filter((course) => canAccessCourse(course, access)),
        [access]
    );

    const categories = useMemo(
        () => visibleCategoriesFor(ALL_COURSES, access),
        [access]
    );

    // BULLETPROOF FILTERING LOGIC
    const visibleCourses = useMemo(() => {
        const safeQuery = (searchQuery || "").toLowerCase();
        return accessibleCourses.filter(course => {
            // Check both label OR title (defensive fix)
            const safeLabel = (course?.label || course?.title || "").toLowerCase();
            const nameMatches = safeLabel.includes(safeQuery);
            const categoryMatches = activeCategory === "All" || course.category === activeCategory;
            return nameMatches && categoryMatches;
        });
    }, [accessibleCourses, searchQuery, activeCategory]);

    const lockedCount = ALL_COURSES.length - accessibleCourses.length;

    return (
        <PageWrapper>
            <BackgroundOrb size={520} sx={{ top: "-130px", left: "-160px", background: "radial-gradient(circle, rgba(218,165,32,0.08) 0%, transparent 70%)" }} />
            <BackgroundOrb size={400} sx={{ bottom: "0", right: "-100px", background: "radial-gradient(circle, rgba(139,92,246,0.07) 0%, transparent 70%)" }} />

            {isMobile && (
                <IconButton
                    onClick={() => setDrawerOpen(true)}
                    sx={{ position: "fixed", top: 14, left: 14, zIndex: 1200, color: COLOR.gold, bgcolor: "rgba(8,6,20,0.85)", border: `1px solid ${COLOR.goldBorder}`, backdropFilter: "blur(10px)" }}
                >
                    <MenuIcon />
                </IconButton>
            )}

            <Drawer
                anchor="left"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: 280, fontFamily: FONT, bgcolor: "#0b0817", color: COLOR.textPrimary, borderRight: `1px solid ${COLOR.goldBorder}` } }}
            >
                <Box sx={{ p: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontWeight: 800, color: COLOR.gold, letterSpacing: "0.14em", fontSize: "0.9rem" }}>MENU</Typography>
                    <IconButton onClick={() => setDrawerOpen(false)} size="small" sx={{ color: COLOR.textSecondary }}><CloseIcon fontSize="small" /></IconButton>
                </Box>
                <Divider sx={{ bgcolor: COLOR.goldBorder }} />
                <List sx={{ pt: 1 }}>
                    {FEATURE_CARDS.map((item, index) => (
                        <ListItem button key={index} component={Link} to={item.link} onClick={() => setDrawerOpen(false)} sx={{ borderRadius: "10px", mx: 1, mb: 0.5, "&:hover": { bgcolor: COLOR.goldGlow } }}>
                            <ListItemIcon sx={{ color: item.color, minWidth: 40 }}>{item.icon}</ListItemIcon>
                            <ListItemText primary={item.label} secondary={item.desc} primaryTypographyProps={{ fontFamily: FONT, fontWeight: 700, fontSize: "0.88rem" }} secondaryTypographyProps={{ color: COLOR.textFaint, fontSize: "0.72rem" }} />
                        </ListItem>
                    ))}
                </List>
            </Drawer>

            <Box sx={{ textAlign: "center", px: 2, mb: 7, position: "relative", zIndex: 1 }}>
                <AnimateIn delay={0} sx={{ mb: 2.5, display: "flex", justifyContent: "center" }}>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2.5, py: 0.9, borderRadius: "999px", bgcolor: COLOR.goldHint, border: `1px solid rgba(218,165,32,0.28)`, fontSize: "0.82rem", color: COLOR.gold }}>
                        {greeting.icon} <span>{greeting.text}, {username}!</span>
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, ml: 1, px: 1.2, py: 0.15, borderRadius: "999px", bgcolor: "rgba(251,146,60,0.15)", color: COLOR.orange, fontSize: "0.74rem", fontWeight: 700 }}>
                            <LocalFireDepartmentIcon sx={{ fontSize: 13 }} /> {streak}-day streak
                        </Box>
                    </Box>
                </AnimateIn>

                <AnimateIn delay={0.1}>
                    <Typography variant={isMobile ? "h4" : "h2"} sx={{ fontWeight: 900, lineHeight: 1.1, mb: 1.5, mt:7.5, background: `linear-gradient(135deg, #ffffff 35%, ${COLOR.gold} 100%)`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                        Developer's Learning Hub
                    </Typography>
                </AnimateIn>

                <AnimateIn delay={0.18}>
                    <Typography sx={{ color: COLOR.textSecondary, fontSize: "0.97rem", mb: 4, maxWidth: 460, mx: "auto" }}>
                        15 hands-on courses · real projects · track your progress
                    </Typography>
                </AnimateIn>

                <AnimateIn delay={0.26} sx={{ maxWidth: 560, mx: "auto", mb: 4 }}>
                    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, textAlign: "left", bgcolor: COLOR.goldHint, border: `1px solid rgba(218,165,32,0.18)`, borderRadius: "14px", px: 3, py: 1.75 }}>
                        <TrendingUpIcon sx={{ color: COLOR.gold, fontSize: 18, mt: 0.35, flexShrink: 0 }} />
                        <Typography sx={{ fontSize: "0.83rem", color: "rgba(255,255,255,0.6)", lineHeight: 1.7 }}><strong style={{ color: COLOR.gold }}>Tip of the day: </strong>{tip}</Typography>
                    </Box>
                </AnimateIn>

                <AnimateIn delay={0.34} sx={{ maxWidth: 480, mx: "auto" }}>
                    <SearchInput fullWidth variant="outlined" placeholder="Search courses…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                                 InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: COLOR.gold, fontSize: 20 }} /></InputAdornment>) }}
                    />
                </AnimateIn>
            </Box>

            <Box sx={{ px: { xs: 2, md: 6 }, mb: 7, position: "relative", zIndex: 1 }}>
                <Grid container spacing={2.5}>
                    {statCards.map((card, index) => (
                        <Grid item xs={6} md={3} key={card.label}><StatCard {...card} delay={0.08 * index} /></Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ px: { xs: 2, md: 6 }, mb: 8, position: "relative", zIndex: 1 }}>
                <SectionHeader eyebrow="Analytics" title="Activity Overview" delay={0.1} />
                <Grid container spacing={3}>
                    <Grid item xs={12} lg={6}>
                        <AnimateIn delay={0.15}>
                            <GlassCard>
                                <CardContent sx={{ p: "24px !important" }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", mb: 2 }}>Active Users Trend</Typography>
                                    <Box sx={{ height: 240 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <AreaChart data={userHistory}>
                                                <defs>
                                                    <linearGradient id="goldAreaFill" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor={COLOR.gold} stopOpacity={0.3} /><stop offset="95%" stopColor={COLOR.gold} stopOpacity={0} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                                <XAxis dataKey="time" tick={{ fill: COLOR.textSecondary, fontSize: 11, fontFamily: FONT }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fill: COLOR.textSecondary, fontSize: 11, fontFamily: FONT }} axisLine={false} tickLine={false} />
                                                <RechartsTooltip content={<ChartTooltip />} />
                                                <Area type="monotone" dataKey="activeUsers" name="Users" stroke={COLOR.gold} strokeWidth={2.5} fill="url(#goldAreaFill)" dot={false} activeDot={{ r: 5, fill: COLOR.gold }} />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </GlassCard>
                        </AnimateIn>
                    </Grid>
                    <Grid item xs={12} lg={6}>
                        <AnimateIn delay={0.22}>
                            <GlassCard>
                                <CardContent sx={{ p: "24px !important" }}>
                                    <Typography sx={{ fontWeight: 700, fontSize: "0.92rem", mb: 2 }}>Projects Submitted Trend</Typography>
                                    <Box sx={{ height: 240 }}>
                                        <ResponsiveContainer width="100%" height="100%">
                                            <BarChart data={projectHistory}>
                                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                                                <XAxis dataKey="time" tick={{ fill: COLOR.textSecondary, fontSize: 11, fontFamily: FONT }} axisLine={false} tickLine={false} />
                                                <YAxis tick={{ fill: COLOR.textSecondary, fontSize: 11, fontFamily: FONT }} axisLine={false} tickLine={false} />
                                                <RechartsTooltip content={<ChartTooltip />} />
                                                <Bar dataKey="projects" name="Projects" fill={COLOR.gold} radius={[6, 6, 0, 0]} maxBarSize={40} />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </Box>
                                </CardContent>
                            </GlassCard>
                        </AnimateIn>
                    </Grid>
                </Grid>
            </Box>

            <Box sx={{ px: { xs: 2, md: 6 }, mb: 8, position: "relative", zIndex: 1 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                    <SectionHeader eyebrow="Curriculum" title={`${username}'s Dashboard`} delay={0.1} />
                    <AnimateIn delay={0.1}>
                        <Typography sx={{ fontSize: "0.75rem", color: COLOR.textFaint, mt: 0.5 }}>
                            {visibleCourses.length} / {accessibleCourses.length} courses
                            {lockedCount > 0 && accessLoaded && (
                                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.4, ml: 1, color: COLOR.textFaint }}>
                                    <LockIcon sx={{ fontSize: 12 }} /> {lockedCount} locked — not in your plan
                                </Box>
                            )}
                        </Typography>
                    </AnimateIn>
                </Box>
                <AnimateIn delay={0.18} sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3, alignItems: "center" }}>
                    <FilterListIcon sx={{ color: COLOR.textSecondary, fontSize: 18, mr: 0.25 }} />
                    {categories.map(category => <CategoryChip key={category} label={category} isActive={activeCategory === category} onClick={() => setActiveCategory(category)} />)}
                </AnimateIn>
                <Grid container spacing={1.5}>
                    {visibleCourses.length > 0 ? (
                        visibleCourses.map((course, index) => (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={course.label || course.title}>
                                <AnimateIn delay={0.03 * index}>
                                    <Link to={course.link} style={{ textDecoration: "none" }}>
                                        <CourseTile>
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: COLOR.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                    {course.label || course.title}
                                                </Typography>
                                                <Typography sx={{ fontSize: "0.69rem", color: COLOR.textFaint, mt: 0.2 }}>{course.category}</Typography>
                                            </Box>
                                            <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: COLOR.gold, flexShrink: 0, animation: `${livePulse} 2.4s ease-in-out infinite` }} />
                                        </CourseTile>
                                    </Link>
                                </AnimateIn>
                            </Grid>
                        ))
                    ) : (
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: "center", py: 9, color: COLOR.textFaint }}>
                                <SearchIcon sx={{ fontSize: 52, mb: 1.5, opacity: 0.35 }} />
                                <Typography sx={{ fontWeight: 700, fontSize: "0.98rem" }}>
                                    {searchQuery ? `No courses match "${searchQuery}"` : "No courses available for your plan yet"}
                                </Typography>
                                <Typography sx={{ fontSize: "0.82rem", mt: 0.5, opacity: 0.7 }}>
                                    {searchQuery
                                        ? 'Try a different keyword or select "All" to reset the filter.'
                                        : "Head to Pricing to pick a track, or contact support if this looks wrong."}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>

            <Box sx={{ px: { xs: 2, md: 6 }, mb: 8, position: "relative", zIndex: 1 }}>
                <SectionHeader eyebrow="Resources" title="Explore Learning Tools" delay={0.1} />
                <Grid container spacing={2.5}>
                    {FEATURE_CARDS.map((item, index) => (
                        <Grid item xs={12} sm={6} md={3} key={item.label}>
                            <AnimateIn delay={0.1 * index}>
                                <Link to={item.link} style={{ textDecoration: "none" }}>
                                    <FeatureTile>
                                        <Avatar sx={{ bgcolor: `${item.color}22`, color: item.color, border: `1px solid ${item.color}44`, width: 64, height: 64, mx: "auto", mb: 2, transition: "transform 0.25s ease", "&:hover": { transform: "scale(1.1)" } }}>
                                            {React.cloneElement(item.icon, { sx: { fontSize: 28 } })}
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 800, fontSize: "0.96rem", color: COLOR.textPrimary, mb: 0.5 }}>{item.label}</Typography>
                                        <Typography sx={{ fontSize: "0.76rem", color: COLOR.textFaint }}>{item.desc}</Typography>
                                    </FeatureTile>
                                </Link>
                            </AnimateIn>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            <Box sx={{ textAlign: "center", px: 2, position: "relative", zIndex: 1, mb: 10 }}>
                <AnimateIn delay={0.1}>
                    <Box sx={{ display: "inline-block", px: { xs: 4, md: 7 }, py: 3, background: `linear-gradient(135deg, rgba(218,165,32,0.11) 0%, rgba(218,165,32,0.03) 100%)`, border: `1px solid rgba(218,165,32,0.22)`, borderRadius: "24px", backdropFilter: "blur(10px)" }}>
                        <Typography sx={{ fontWeight: 800, fontSize: isMobile ? "1rem" : "1.1rem", color: COLOR.gold, mb: 0.5 }}>Keep building. Keep shipping. 🚀</Typography>
                        <Typography sx={{ fontSize: "0.75rem", color: COLOR.textFaint }}>{new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}</Typography>
                    </Box>
                </AnimateIn>
            </Box>

            <ScrollToTopButton />
        </PageWrapper>
    );
};

export default CourseHubDashboard;
